import { ListStore } from '@list-store';
import { debounce, DebouncedFunc, differenceBy, some, uniqBy } from 'lodash';
import {
    HoldEventUnitView,
    HoldEventUpdateForm,
    HoldEventEventView,
    HoldEventUnitGroupEnumType,
    HoldEventUnitGroupEnum,
    UnitFiltersForm,
    HoldEventResource,
} from '@mark43/cad-api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { globalAttributes } from '../common/global-attributes';
import { useAttributesByType } from '../attributes/use-attributes-by-type';
import { useDispatchAreaStore } from '../dispatch-areas/use-dispatch-areas';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { createSortComparator } from '../manage-units/common/create-sort-comparator';
import { tokenize } from '../manage-units/common/tokenize';
import { numberComparator } from '../common/sorting/comparators';
import { SortDirection } from '../common/sorting/types';
import {
    sortingConfig,
    SortingParameterEnum,
    SortingParameterName,
} from '../manage-units/common/sorting-config';
import { groupUnitTypeIdsByClass } from '../common/helpers/unit-types';

interface UseHoldEventDrawerDataReturnType {
    store: ListStore<HoldEventUnitView>;
    event?: HoldEventEventView;
    debouncedSetQuery: DebouncedFunc<React.Dispatch<string>>;
    sortDirection: SortDirection;
    setSortDirection: React.Dispatch<SortDirection>;
    sortingParam: SortingParameterName;
    setSortingParam: React.Dispatch<SortingParameterName>;
    selectedFilters: UnitFiltersForm;
    hasSelectedFilters: boolean;
    setSelectedFilters: React.Dispatch<UnitFiltersForm>;
    initiallySelectedUnitIds: number[];
    selectedUnitIds: number[];
    toggleSelectUnit: (unit: HoldEventUnitView) => void;
    initiallySelectedUnits: HoldEventUnitView[];
    selectedUnits: HoldEventUnitView[];
    unheldUnits: HoldEventUnitView[];
    submit: () => void;
    isEventQueryLoading: boolean;
    isUnitsQueryLoading: boolean;
    isQueryError: boolean;
    isMutationLoading: boolean;
    isMutationError: boolean;
}

export function useHoldEventDrawerData(eventId: number): UseHoldEventDrawerDataReturnType {
    const drawerControls = useDrawerControls();
    const { attributes: unitTypeAttributes } = useAttributesByType({ attributeType: 'UNIT_TYPE' });
    const dispatchAreaStore = useDispatchAreaStore();
    const [event, setEvent] = useState<HoldEventEventView>();
    const [selectedUnits, setSelectedUnits] = useState<HoldEventUnitView[]>([]);

    const selectedUnitIds = useMemo(() => {
        return selectedUnits.map((u) => u.id);
    }, [selectedUnits]);

    const defaultFilters: UnitFiltersForm = useMemo(() => {
        return {
            dispatchAreaIds: dispatchAreaStore.monitoredAndOwnedDispatchAreaIds,
            policeUnitTypeIds: [],
            fireUnitTypeIds: [],
            emsUnitTypeIds: [],
            skillIds: [],
            equipmentIds: [],
        };
    }, [dispatchAreaStore.monitoredAndOwnedDispatchAreaIds]);

    const [selectedFilters, setSelectedFilters] = useState<UnitFiltersForm>(defaultFilters);

    const hasSelectedFilters = useMemo(() => {
        for (const filters in Object.values(selectedFilters)) {
            if (filters.length > 0) {
                return true;
            }
        }
        return false;
    }, [selectedFilters]);

    const [query, setQuery] = useState<string>('');
    const [sortingParam, setSortingParam] = useState<SortingParameterName>(
        SortingParameterEnum.CALL_SIGN
    );
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const apiClient = useApiClient();

    const groupingDirective = useMemo(() => {
        return {
            getGroupValue: (row: HoldEventUnitView): HoldEventUnitGroupEnumType =>
                row.holdEventUnitGroup === 'OTHER_UNITS'
                    ? selectedUnitIds.includes(row.id)
                        ? 'TO_HOLD'
                        : row.holdEventUnitGroup
                    : row.holdEventUnitGroup,
            comparator: (left: HoldEventUnitGroupEnumType, right: HoldEventUnitGroupEnumType) => {
                return numberComparator(
                    HoldEventUnitGroupEnum[left].index,
                    HoldEventUnitGroupEnum[right].index
                );
            },
        };
    }, [selectedUnitIds]);

    const [store] = useState(() => {
        return new ListStore<HoldEventUnitView>({ groupingDirective });
    });

    useEffect(() => {
        store.setGroupingDirective(groupingDirective);
    }, [store, groupingDirective]);

    const debouncedSetQuery = React.useMemo(() => debounce(setQuery, 100), [setQuery]);
    const [hasSetInitialFilters, setHasSetInitialFilters] = useState(false);

    const eventQuery = useQuery(
        ['hold-event-event', eventId, defaultFilters] as const,
        ({ queryKey }) => {
            return apiClient.request<HoldEventResource.GetHoldEventResponseByEvent>({
                method: 'POST',
                url: 'hold_event/event/',
                data: { ...queryKey[2], eventId: queryKey[1], selectedUnitIds: [] },
            });
        }
    );
    const holdEventEventResponse = eventQuery.data?.data.data;
    // hits the same endpoint as the event request, but convenient to separate loading
    // behaviors for units on filter change
    const unitsQuery = useQuery(
        ['hold-event-units', eventId, selectedFilters] as const,
        ({ queryKey }) => {
            return apiClient.request<HoldEventResource.GetHoldEventResponseByEvent>({
                method: 'POST',
                url: 'hold_event/event/',
                data: {
                    ...queryKey[2],
                    eventId: queryKey[1],
                    selectedUnitIds,
                },
            });
        },
        {
            enabled: !!selectedFilters && hasSetInitialFilters,
        }
    );
    const holdEventUnitsResponse = unitsQuery.data?.data.data;

    useEffect(
        function storeEvent() {
            if (holdEventEventResponse?.event) {
                setEvent(holdEventEventResponse.event);
            }
        },
        [holdEventEventResponse, setEvent]
    );

    const [hasSetInitiallySelectedUnits, setHasSetInitiallySelectedUnits] = useState(false);

    useEffect(
        function storeUnits() {
            if (holdEventUnitsResponse?.units) {
                store.setItems(holdEventUnitsResponse.units);
            }
            if (holdEventUnitsResponse?.initiallySelectedUnits) {
                store.upsertItems(holdEventUnitsResponse.initiallySelectedUnits);
                if (!hasSetInitiallySelectedUnits) {
                    setSelectedUnits(holdEventUnitsResponse.initiallySelectedUnits);
                    setHasSetInitiallySelectedUnits(true);
                }
            }
        },
        [holdEventUnitsResponse, store, hasSetInitiallySelectedUnits, selectedUnits]
    );

    const setInitialFilters = useCallback(
        (filters: UnitFiltersForm) => {
            if (!hasSetInitialFilters) {
                setSelectedFilters(filters);
                setHasSetInitialFilters(true);
            }
        },
        [hasSetInitialFilters, setHasSetInitialFilters, setSelectedFilters]
    );

    useEffect(() => {
        if (hasSetInitialFilters || !unitTypeAttributes || !holdEventEventResponse?.event) {
            return;
        }

        const { policeUnitTypeIds, fireUnitTypeIds, emsUnitTypeIds } = groupUnitTypeIdsByClass(
            unitTypeAttributes
        );

        const { agencyTypeGlobalAttrId } = holdEventEventResponse.event;
        const isPoliceEvent = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;
        const isFireEvent = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.FIRE;
        const isEmsEvent = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.EMS;

        setInitialFilters({
            ...defaultFilters,
            policeUnitTypeIds: isPoliceEvent ? policeUnitTypeIds : [],
            fireUnitTypeIds: isFireEvent ? fireUnitTypeIds : [],
            emsUnitTypeIds: isEmsEvent ? emsUnitTypeIds : [],
        });
    }, [
        holdEventEventResponse,
        defaultFilters,
        selectedFilters,
        hasSetInitialFilters,
        setInitialFilters,
        unitTypeAttributes,
    ]);

    const initiallySelectedUnits = useMemo(() => {
        return holdEventUnitsResponse?.initiallySelectedUnits ?? [];
    }, [holdEventUnitsResponse]);

    const initiallySelectedUnitIds = useMemo(() => {
        return initiallySelectedUnits.map((u) => u.id);
    }, [initiallySelectedUnits]);

    const unheldUnits = useMemo(() => {
        return differenceBy(initiallySelectedUnits, selectedUnits, (unit) => unit.id);
    }, [selectedUnits, initiallySelectedUnits]);

    useEffect(() => {
        store.setFilterDirective((unit) => {
            return (
                selectedUnitIds.includes(unit.id) ||
                (query ? some(tokenize(query), (token) => unit.searchValue.includes(token)) : true)
            );
        });
    }, [store, query, selectedUnitIds]);

    useEffect(() => {
        const sortComparator = createSortComparator(
            sortDirection,
            sortingConfig[sortingParam].sortType
        );
        store.setSortingDirective({
            getSortValue: (item) => {
                return sortingConfig[sortingParam].getSortValue(item);
            },
            comparator: (left: string | number | undefined, right: string | number | undefined) => {
                return sortComparator(left, right);
            },
        });
    }, [store, sortDirection, sortingParam]);

    const toggleSelectUnit = useCallback(
        (unit: HoldEventUnitView) => {
            if (selectedUnitIds.includes(unit.id)) {
                setSelectedUnits((prev) => prev.filter((u) => u.id !== unit.id));
            } else {
                setSelectedUnits((prev) => {
                    return uniqBy([...prev, unit], (u) => u.id);
                });
            }
        },
        [selectedUnitIds, setSelectedUnits]
    );

    const mutation = useMutation(
        (holdEventUpdateForm: HoldEventUpdateForm) => {
            return apiClient.request<HoldEventResource.UpdateFromHoldEventUpdateForm>({
                method: 'POST',
                url: 'hold_event',
                data: holdEventUpdateForm,
            });
        },
        {
            onSuccess() {
                drawerControls.close();
            },
        }
    );

    const submit = useCallback(() => {
        mutation.mutate({ eventId, unitIds: selectedUnitIds });
    }, [mutation, eventId, selectedUnitIds]);

    return {
        store,
        submit,
        debouncedSetQuery,
        selectedFilters,
        initiallySelectedUnitIds,
        initiallySelectedUnits,
        selectedUnitIds,
        selectedUnits,
        unheldUnits,
        setSelectedFilters,
        hasSelectedFilters,
        sortingParam,
        setSortingParam,
        setSortDirection,
        sortDirection,
        toggleSelectUnit,
        event,
        isEventQueryLoading: eventQuery.isInitialLoading,
        isUnitsQueryLoading: unitsQuery.isInitialLoading,
        isQueryError: eventQuery.isError || unitsQuery.isError,
        isMutationLoading: mutation.isLoading,
        isMutationError: mutation.isError,
    };
}
